import {
  Box,
  Button,
  CircularProgress,
  Container,
  Card,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import FormTitle from "../../components/common/form/FormTitle";
import FormSmallTextField from "../../components/common/form/FormTextField";
import AppDropDown from "../../components/common/filter/AppDropDown";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import { status_list } from "../../../models/TaskTracker/TaskStatus";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { OrderProducts } from "../../../models/TaskTracker/OrderProducts";
import { Admin } from "../../../models/TaskTracker/Admin";
import { priority_list } from "../../../models/TaskTracker/TaskPriority";
import { useNotification } from "../../../utils/NotificationsContext";
import { useLocation, useNavigate } from "react-router-dom";
import { TaskAssignee } from "../../../models/TaskTracker/TaskAssignee";
import { Task } from "../../../models/TaskTracker/Task";
import { TaskProduct } from "../../../models/TaskTracker/TaskProduct";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import RichTextEditor from "../../components/common/textEditor/RichTextEditor";

interface AddEditTaskProps {
  isModal?: boolean;
  onClose?: () => void;
  orderId?: number;
  taskId?: number;
  isNavigationNeeded?: boolean;
  onSuccess?: (taskData: Task) => void;
  onError?: (error: any) => void;
}

const AddEditTask: React.FC<AddEditTaskProps> = ({
  isModal = false,
  onClose,
  orderId: propOrderId,
  taskId: propTaskId,
  isNavigationNeeded = true,
  onSuccess,
  onError,
}) => {
  const [taskData, setTaskData] = useState<Task>();
  const { TaskTrackerStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const taskId =
    propTaskId ||
    (queryParams.get("task-id") ? Number(queryParams.get("task-id")) : 0);
  const orderId =
    propOrderId ||
    (queryParams.get("order-id")
      ? Number(queryParams.get("order-id"))
      : null) ||
    (queryParams.get("order_id") ? Number(queryParams.get("order_id")) : null);
  const inputAssigneeTextRef = useRef<string>("");
  const inputOrderTextRef = useRef<string>("");
  const [, setSearchTrigger] = useState<number>(0);
  let assigneeTimer: string | number | NodeJS.Timeout | null | undefined = null;
  let orderTimer: string | number | NodeJS.Timeout | null | undefined = null;
  const [isDisabled, setIsDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const setTaskDataFromStore = () => {
    setTaskData({
      id: Number(taskId),
      order_id: TaskTrackerStore.taskDetails?.order_id,
      title: TaskTrackerStore.taskDetails?.title,
      due_date: TaskTrackerStore.taskDetails?.due_date!,
      status: TaskTrackerStore.taskDetails?.status,
      priority: TaskTrackerStore.taskDetails?.priority,
      description: TaskTrackerStore.taskDetails?.description,
      order_details: TaskTrackerStore.taskDetails?.order_details,
      archived: TaskTrackerStore.taskDetails?.archived,
      assignees: TaskTrackerStore.taskDetails?.assignees,
      products: TaskTrackerStore.taskDetails?.products,
    });
    if (TaskTrackerStore.taskDetails?.order_id) {
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      TaskTrackerStore.getTaskDetailsById(Number(taskId))
        .then(() => {
          if (TaskTrackerStore.taskDetails?.order_id) {
            TaskTrackerStore.getOrderProducts(
              TaskTrackerStore.taskDetails.order_id
            );
          }
          setTaskDataFromStore();
        })
        .catch((err) => {
          if (!isModal) {
            showError(
              "Error occurred",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later."
            );
          }
        });
    } else {
      const order_id = Number(orderId);
      if (order_id) {
        setTaskData({
          order_id: Number(orderId),
        });

        TaskTrackerStore.getOrderProducts(order_id);
        setIsDisabled(false);
      } else {
        setTaskData({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, orderId]);

  const getTodayDate = () => {
    const today = new Date();
    const dateStr = today.toISOString().split("T")[0];
    updateTaskDataDetails({ due_date: dateStr });
    return dateStr;
  };

  const handleInputChange = (name?: string, value?: string) => {
    updateTaskDataDetails({ [name as string]: value });
  };

  const handleProductsSelectChange = (
    products: TaskProduct[],
    name: string
  ) => {
    updateTaskDataDetails({ [name]: products });
  };

  const handleSingleSelectChange = (id: string, name: string) => {
    updateTaskDataDetails({ [name]: isNaN(Number(id)) ? id : parseInt(id) });
  };

  const searchOrderId = (orderId: number) => {
    inputOrderTextRef.current = orderId.toString();
    setSearchTrigger((prev) => prev + 1);
    if (orderTimer) {
      clearTimeout(orderTimer);
    }

    if (orderId.toString().length > 2) {
      assigneeTimer = setTimeout(() => {
        TaskTrackerStore.getProjectOrderIds(orderId).catch((err) =>
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          )
        );
      }, 700);
    }
  };

  const searchAssignee = (name: string) => {
    inputAssigneeTextRef.current = name;
    setSearchTrigger((prev) => prev + 1);
    if (assigneeTimer) {
      clearTimeout(assigneeTimer);
    }

    if (name.length > 2) {
      assigneeTimer = setTimeout(() => {
        TaskTrackerStore.getAdminBySearch(name).catch((err) =>
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          )
        );
      }, 700);
    }
  };

  const selectedAssignees = (admins: TaskAssignee[]) => {
    inputAssigneeTextRef.current = "";
    admins = admins.map((admin) => ({
      id: admin.admin_id,
      name: admin.name,
    }));
    updateTaskDataDetails({ assignees: admins });
  };

  const updateTaskDataDetails = (newData: any) => {
    setTaskData((prevData: any) => ({
      ...prevData,
      ...newData,
    }));
  };

  const applyTask = () => {
    setLoading(() => true);
    if (taskId) {
      updateTask();
    } else {
      createTask();
    }
  };

  const createTask = () => {
    if (taskData) {
      const taskDataWithDefaults: Task = {
        ...taskData,
        status: taskData.status || 1,
      };

      if (!taskDataWithDefaults.order_id) {
        delete taskDataWithDefaults.order_id;
        delete taskDataWithDefaults.products;
      }

      TaskTrackerStore.createTask(taskDataWithDefaults)
        .then((response) => {
          setLoading(false);
          if (isModal) {
            if (onSuccess) {
              onSuccess(taskDataWithDefaults);
            }
            if (onClose) {
              onClose();
            }
          } else {
            showSuccess("Task created successfully");
            if (isNavigationNeeded) {
              navigate("/task-tracker/listing");
            }
          }
        })
        .catch((err) => {
          setLoading(false);
          if (isModal && onError) {
            onError(err);
          } else if (!isModal) {
            showError(
              "Error occurred",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later."
            );
          }
        });
    } else {
      setLoading(false);
    }
  };

  const updateTask = () => {
    if (taskData) {
      TaskTrackerStore.updateTaskDetails(taskData)
        .then(() => {
          setLoading(false);
          if (isModal) {
            if (onSuccess) {
              onSuccess(taskData);
            }
            if (onClose) {
              onClose();
            }
          } else {
            showSuccess("Task updated successfully");
          }
        })
        .catch((err) => {
          setLoading(false);
          if (isModal && onError) {
            onError(err);
          } else if (!isModal) {
            showError(
              "Error occurred",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later."
            );
          }
        });
    } else {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateString: string | undefined) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  return taskData ? (
    <Box sx={{ px: "30px", py: "20px" }}>
      <Container maxWidth="xl">
        {!isModal && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              cursor: "pointer",
              color: theme.palette.customColors.greyTextColor,
              mb: 1,

              maxWidth: "3.75rem",
            }}
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowBackIcon sx={{ fontSize: "1rem" }} />
            <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>
              Back
            </span>
          </Box>
        )}
        <Box>
          <CustomTypography
            variant="heading3"
            color="primary"
            style={{ marginBottom: "25px" }}
          >
            {taskId ? "Update Task" : "Create Task"}
          </CustomTypography>

          <Box sx={{ width: "100%", marginRight: 4 }}>
            <Card elevation={1} sx={{ px: 6, py: 4, mb: 4 }}>
              <FormTitle title="Title" />
              <FormSmallTextField
                required
                id="title"
                label="Task Title here "
                variant="outlined"
                fontSize="14px"
                onChange={handleInputChange}
                value={taskData?.title || ""}
                borderColor={theme.palette.customColors.greyTextColor}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "20px",
                  paddingBottom: "20px",
                }}
              >
                <Box sx={{ width: "45%" }}>
                  <FormTitle title="Assigned To" />
                  <AppSearchDropDown
                    isMultiSelect={true}
                    title="Select assignee *"
                    list={
                      Array.isArray(TaskTrackerStore?.admins)
                        ? TaskTrackerStore.admins.map((admin: Admin) => ({
                            id: admin.id.toString(),
                            value: admin.name.toString(),
                          }))
                        : []
                    }
                    local={false}
                    fontSize="14px"
                    handleSearch={(name) => {
                      searchAssignee(name!);
                    }}
                    handleSelect={(selectedAdmin) => {
                      selectedAssignees(
                        selectedAdmin.map((admin) => ({
                          admin_id: Number(admin.id),
                          name: admin.value,
                        }))
                      );
                    }}
                    textFieldValue={inputAssigneeTextRef.current || ""}
                    value={
                      Array.isArray(taskData.assignees)
                        ? taskData?.assignees?.map((assignee) => ({
                            id: assignee.id!?.toString(),
                            value: assignee.name!?.toString(),
                          }))
                        : []
                    }
                    borderColor={theme.palette.customColors.greyTextColor}
                  />
                </Box>

                <Box sx={{ width: "45%" }}>
                  <FormTitle title="Status" />
                  <AppDropDown
                    title="Select task status *"
                    isMultiSelect={false}
                    value={taskData?.status?.toString() || "1"}
                    list={status_list}
                    height="36.8px"
                    listHeight="30px"
                    onSingleSelectionChange={handleSingleSelectChange}
                    id="status"
                    borderColor={theme.palette.customColors.greyTextColor}
                    borderRadius="4px"
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingBottom: "20px",
                }}
              >
                <Box sx={{ width: "45%" }}>
                  <FormTitle title="Order ID" />
                  <AppSearchDropDown
                    isMultiSelect={false}
                    title="Select order id"
                    borderColor={theme.palette.customColors.greyTextColor}
                    value={
                      taskData?.order_id
                        ? [
                            {
                              id: taskData.order_id!?.toString(),
                              value: taskData.order_id!?.toString(),
                            },
                          ]
                        : []
                    }
                    list={
                      TaskTrackerStore?.orderIds?.map((orderId) => ({
                        id: orderId.toString(),
                        value: orderId.toString(),
                      })) || []
                    }
                    textFieldValue={inputOrderTextRef.current}
                    local={false}
                    fontSize="14px"
                    handleSearch={(orderId) => searchOrderId(Number(orderId))}
                    handleSelect={(selectedOrderId) => {
                      if (selectedOrderId && selectedOrderId.length > 0) {
                        const selectedId = selectedOrderId[0].id;
                        setTaskData((prevData) => ({
                          ...prevData,
                          order_id: Number(selectedId),
                          products: [],
                        }));
                        TaskTrackerStore.getOrderProducts(Number(selectedId));
                        setIsDisabled(false);
                        inputOrderTextRef.current = "";
                        return;
                      }

                      setTaskData((prevData) => ({
                        ...prevData,
                        order_id: undefined,
                        products: [],
                      }));
                      setIsDisabled(true);
                      inputOrderTextRef.current = "";
                    }}
                  />
                </Box>

                <Box sx={{ width: "45%" }}>
                  <FormTitle title="Products" />
                  <AppDropDown
                    title="Select products"
                    placeholderText="Select products"
                    isMultiSelect={true}
                    list={
                      TaskTrackerStore?.products?.map(
                        (product: OrderProducts) => ({
                          id: product.id!.toString(),
                          value: product.name!.toString(),
                        })
                      ) || []
                    }
                    height="36.8px"
                    listHeight="30px"
                    onMultiSelectionChange={(ids, name) => {
                      const products = ids.map((id) => {
                        return {
                          id: Number(id),
                        } as TaskProduct;
                      }) as TaskProduct[];
                      handleProductsSelectChange(products, name);
                    }}
                    id="products"
                    valuesList={taskData.products?.map((product) =>
                      product.id?.toString()
                    )}
                    clearSelectedValuesByUniqueId={taskData.order_id}
                    disabled={isDisabled}
                    borderColor={theme.palette.customColors.greyTextColor}
                    borderRadius="4px"
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ width: "45%" }}>
                  <FormTitle title="Due Date *" />
                  <FormSmallTextField
                    id="due_date"
                    label=""
                    required
                    variant="outlined"
                    color="secondary"
                    type="date"
                    fontSize="14px"
                    onChange={handleInputChange}
                    value={
                      taskData.due_date
                        ? formatDateForInput(taskData.due_date?.toString())
                        : getTodayDate()
                    }
                    borderColor={theme.palette.customColors.greyTextColor}
                  />
                </Box>

                <Box sx={{ width: "45%" }}>
                  <FormTitle title="Priority" />
                  <AppDropDown
                    title="Select priority *"
                    isMultiSelect={false}
                    value={taskData?.priority?.toString() || ""}
                    list={priority_list.map((status) => status)}
                    height="36.8px"
                    listHeight="30px"
                    onSingleSelectionChange={handleSingleSelectChange}
                    id="priority"
                    borderColor={theme.palette.customColors.greyTextColor}
                    borderRadius="4px"
                  />
                </Box>
              </Box>

              <FormTitle title="Description" />
              <Box mt={3} mb={4}>
                <RichTextEditor
                  value={taskData?.description || ""}
                  onChange={(value: string) =>
                    handleInputChange("description", String(value))
                  }
                  maxLength={500}
                  placeholder="Please write task description here..."
                  minHeight="110px"
                />
                {/* <FormSmallTextField
                  id="description"
                  label="Add task description"
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  onChange={handleInputChange}
                  value={taskData?.description}
                /> */}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  paddingBottom: "20px",
                }}
              >
                {loading ? (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ minHeight: "50px", padding: "6px 16px" }}
                    fullWidth
                    onClick={() => applyTask()}
                    disabled={loading}
                  >
                    <CircularProgress size={24} color="inherit" />
                  </Button>
                ) : (
                  <AppButton
                    variant="primary"
                    onClick={() => applyTask()}
                    disabled={loading}
                    label={taskId ? "Update Task" : "Create Task"}
                    customStyles={{ width: "20vw", height: "40px" }}
                  />
                )}
              </Box>
            </Card>
          </Box>
        </Box>
      </Container>
    </Box>
  ) : (
    <></>
  );
};

export default observer(AddEditTask);
